 
import { globalDexieDb } from './DexiedbLib';
import { MessageParseState, WireMessage } from "../websocket/wsTypes";
import { createDefaultChatSyncState, IChatSyncState } from './model/chatSyncState';
import { GetUserFullInfo } from '../utils/common';
import ApiHelper from '../restapi/ApiHelper';
import { MediaContent } from '../../../api/types/messages';

// Record lưu từng message trong IndexedDB

export interface TelegramMessageRecord {
    id: number;
    content?: MediaContent;
    ownerId: string;
    chatId: number;
    messageId: number;

    // fields từ WireMessage
    senderId: number;
    date: number;         // unix seconds
    text: string;
    isOutgoing: boolean;
    chatTitle: string;

    // thêm cho tiện UI
    createdAt: number;    // epoch ms lưu lúc insert
    parseState: MessageParseState; // message_id từ lotteryvn (tăng dần, dùng để sync)
    isAutoParse?: boolean; // đánh dấu tự động parse hay không
}
async function getOrCreateChatSyncState(
    ownerId: string,
    chatId: number,
): Promise<IChatSyncState> {
    let state = await globalDexieDb.chatSyncState.get([ownerId, chatId]);
    if (!state) {
        state = createDefaultChatSyncState(ownerId, chatId);
        await globalDexieDb.chatSyncState.put(state);
    }
    return state;
}
// =====================
// 5. Helper: lưu message
// =====================

function wireToRecord(
    ownerId: string,
    wire: WireMessage,
): TelegramMessageRecord {
    return {
        ownerId,
        chatId: wire.chat_id,
        messageId: wire.message_id,
        senderId: wire.sender_id,
        date: wire.date,
        text: wire.text,
        isOutgoing: wire.is_outgoing,
        chatTitle: wire.chat_title,
        createdAt: Date.now(),
        parseState: wire.parse_state,
    };
}
function wireToRecordCamelCase(
    ownerId: string,
    wire: any,
): TelegramMessageRecord {
    return {
        ownerId,
        chatId: wire.chatId,
        messageId: wire.messageId,
        senderId: wire.senderId,
        date: wire.date,
        text: wire.text,
        isOutgoing: wire.isOutgoing,
        chatTitle: '',
        createdAt: Date.now(),
        parseState: wire.parseState,
    };
}
/**
 * Upsert 1 message vào IndexedDB + cập nhật ChatSyncState nếu cần
 */
export async function upsertMessageFromWire(
    ownerId: string,
    wire: WireMessage,
): Promise<void> {
    const rec = wireToRecord(ownerId, wire);

    await globalDexieDb.transaction('readwrite', globalDexieDb.telegramMessage, globalDexieDb.chatSyncState, async () => {
        // upsert message
        await globalDexieDb.telegramMessage.put(rec);

        const current = await getOrCreateChatSyncState(ownerId, rec.chatId);
        current.chatTitle = rec.chatTitle; // cập nhật title mới nhất
        // cập nhật lastSyncedMessageId nếu cần
        if (wire.message_id > current.lastSyncedMessageId) {
            await globalDexieDb.chatSyncState.put({
                ...current,
                lastSyncedMessageId: wire.message_id,
                lastSyncedAt: Date.now(),
            });
        }
        // cập nhật vào db lưu message cũ (dành cho các module khác nếu cần)
        await updateOldTelegramMessageRecord(
            ownerId,
            rec.chatId,
            rec.messageId,
            {
                date: rec.date,
                messageId: rec.messageId,
                text: rec.text,
            }
        );
    });
}
function updateOldTelegramMessageRecord(
    ownerId: string,
    chatId: number,
    messageId: number,
    patch: Partial<TelegramMessageRecord>,
): Promise<void> {
    const oldMessage = {
        chatId: chatId.toString(),
        date: patch.date,
        id: messageId,
        content: { text: patch.text },
       
    };
    globalDexieDb.transaction('readwrite', globalDexieDb.messages, async () => {
        await globalDexieDb.messages.put(oldMessage as any);
    });
    return Promise.resolve();
}
// =====================
// 6. Helper: cập nhật trạng thái sync của chat
// =====================
export
    async function updateChatSyncState(
        ownerId: string,
        chatId: number,
        patch: Partial<IChatSyncState>,
    ): Promise<void> {
    const current = await getOrCreateChatSyncState(ownerId, chatId);
    const next: IChatSyncState = {
        ...current,
        ...patch,
        ownerId,
        chatId,
    };
    await globalDexieDb.chatSyncState.put(next);
}
export async function syncTeleMessages({ chat_id }: { chat_id: number }): Promise<any> {
    const userInfo = GetUserFullInfo();
    const payload = {
        chat_id,
        owner_id: userInfo.username,
        client_ids: [] as number[],
    };

    // 1. Lấy toàn bộ message client đã có
    const messages = await globalDexieDb.telegramMessage
        .where("[ownerId+chatId]")
        .equals([userInfo.username, chat_id])
        .toArray();

    payload.client_ids = messages.map(m => m.messageId);

    // 2. Gửi lên server để lấy danh sách message thiếu
    const result = await ApiHelper.post("bot_message/missing_messages", payload) as any;

    const missingMessages = result?.data?.missing_ids as WireMessage[] || [];

    if (!Array.isArray(missingMessages) || missingMessages.length === 0) {
        console.log("No missing messages to sync.");
        return result;
    }

    // 3. Chuyển đổi sang định dạng lưu trữ local
    const records = missingMessages.map(msg => wireToRecordCamelCase(userInfo.username, msg));

    // 4. Lưu bulk vào Dexie
    try {
        await globalDexieDb.transaction('rw', globalDexieDb.telegramMessage, async () => {
            await globalDexieDb.telegramMessage.bulkPut(records);
        });
    } catch (err) {
        console.error("Lỗi khi lưu bulk messages vào DexieDB:", err);
    }

    return records;
}

export async function syncChats(): Promise<IChatSyncState[]> {
    const userInfo = GetUserFullInfo();
    const payload = {
        owner_id: userInfo.username,         
    };
    const result = await ApiHelper.post("bot_message/sync_chats", payload) as any;
    const chatList = result?.data?.chat_list as any[]||[];
    const records = chatList.map(chat => {  
        const ret =createDefaultChatSyncState(userInfo.username, chat.chatId);
        ret.chatTitle = chat.title;
        return ret;
     }
    )
    // 4. Lưu bulk vào Dexie
    try {
        await globalDexieDb.transaction('rw', globalDexieDb.chatSyncState, async () => {
            await globalDexieDb.chatSyncState.bulkPut(records);
        });
    } catch (err) {
        console.error("Lỗi khi lưu bulk messages vào DexieDB:", err);
    }

    return records;
}
